import {
  ColorSwatch,
  FormControlLabel,
  Toggle,
  Typography
} from '@customink/pigment-react';
import {styled} from '@customink/pigment-react/lib/themeCustomink';
import PropTypes from 'prop-types';
import * as React from 'react';
import {stylesRgbToHex} from 'utils/color';

const QuickViewCell = styled('td')({
  padding: '5px 0.375rem'
});

const QuickViewColors = styled('div')({
  backgroundColor: 'rgba(255,255,255,0.95)',
  display: 'inline-block',
  order: '-1',
  position: 'relative',
  width: '100%'
});

const QuickViewNonInteractiveSwatch = styled('span')(({bgColor}) => ({
  backgroundColor: bgColor,
  border: `1px solid #00000091`,
  borderRadius: '3px',
  display: 'inline-block',
  height: '18px',
  margin: '0 1%',
  overflow: 'hidden',
  position: 'relative',
  top: '0.25rem',
  width: '18px'
}));

const QuickViewOrderQty = styled('div')({
  borderRight: 'none',
  display: 'flex',
  float: 'right',
  justifyContent: 'flex-start',
  marginRight: '1.5rem',
  position: 'inherit'
});

const QuickViewSwatchContainer = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '0.5rem',
  justifyContent: 'flex-start',
  margin: '0.5rem 0'
});

const QuickViewColorSwatches = ({colors}) => {
  const validColors = colors.filter((color) => color.status !== 'retired');
  const [minQtyEnabled, setMinQtyEnabled] = React.useState(false);
  const [selectedColor, setSelectedColor] = React.useState(validColors[0]);
  const [activeColors, setActiveColors] = React.useState(validColors);

  const hasNoMinimum = validColors.some((color) => color.singles_enabled);

  React.useEffect(() => {
    if (minQtyEnabled) {
      const availableColors = validColors.filter(
        (color) => color.singles_enabled
      );
      setActiveColors(availableColors);

      // Check if selectedColor exists in availableColors
      const isSelectedColorAvailable = availableColors.some(
        (color) => color.name === selectedColor.name
      );
      if (!isSelectedColorAvailable) {
        setSelectedColor(availableColors[0]);
      }
    } else {
      setActiveColors(validColors);
    }
  }, [colors, minQtyEnabled, selectedColor]);

  const handleMinQtyToggle = () => {
    setMinQtyEnabled(!minQtyEnabled);
  };

  const handleSwatchClick = (color) => {
    setSelectedColor(color);
  };

  const colorSwatches = activeColors.map((color) => (
    <ColorSwatch
      color={stylesRgbToHex(color.rgbs[0])}
      value={color.id}
      key={`${color.name} - ${color.id}`}
      selected={color.id === selectedColor.id}
      onClick={() => handleSwatchClick(color)}
      title={color.name}
      size="small"
      sx={{
        border: '1px solid rgba(0, 0, 0, 0.15)'
      }}
    />
  ));

  if (activeColors.length === 0) {
    return null;
  }

  return (
    <tr>
      <QuickViewCell>Total Colors</QuickViewCell>
      <QuickViewCell>
        <Typography data-testid="quickview-colors-count">
          {activeColors.length}
        </Typography>
        <QuickViewColors>
          Selected Color:
          <QuickViewNonInteractiveSwatch
            data-testid="quickview-active-swatch"
            bgColor={stylesRgbToHex(selectedColor.rgbs[0])}
          />
          <Typography>{selectedColor.name}</Typography>
          <QuickViewOrderQty>
            <FormControlLabel
              control={
                <Toggle
                  data-testid="quickview-min-qty-toggle"
                  data-active={minQtyEnabled}
                  onChange={handleMinQtyToggle}
                  checked={minQtyEnabled}
                  disabled={!hasNoMinimum}
                />
              }
              label={
                <Typography sx={{fontSize: '16px'}}>No Minimum</Typography>
              }
              labelPlacement="start"
              sx={{
                gap: '0.5rem',
                padding: '0'
              }}
            />
          </QuickViewOrderQty>
        </QuickViewColors>
        <QuickViewSwatchContainer>{colorSwatches}</QuickViewSwatchContainer>
      </QuickViewCell>
    </tr>
  );
};

QuickViewColorSwatches.propTypes = {
  colors: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      rgbs: PropTypes.arrayOf(PropTypes.object).isRequired,
      singles_enabled: PropTypes.bool,
      status: PropTypes.string
    })
  ).isRequired
};

export default QuickViewColorSwatches;
